// TODO: creator-camp-cleanup
import {
  Badge,
  Button,
  CopyIcon,
  Flex,
  IconButton,
  InfoIcon,
  Link,
  PromotionalBanner,
  Text,
  useMediaQuery,
} from '@buffer-mono/popcorn'
import styles from './CreatorCamp.module.css'
import { ToggleTip } from '../ToggleTip'
import {
  TemplateMiniCard,
  TemplateMiniCardLink,
} from '../Template/TemplateMiniCard'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import type { CreatorCampTemplate } from '../TemplateLibraryPage/TemplateLibraryPage'
import clsx from 'clsx'
import { useIsSplitEnabled } from '@buffer-mono/features'

const CardIllustration = (): JSX.Element => (
  <svg
    width="186"
    height="164"
    viewBox="0 0 186 164"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.76367 0.948242H179.236C182.696 0.948242 185.5 3.7526 185.5 7.21191V157.236C185.5 160.696 182.696 163.5 179.236 163.5H6.76367C3.30436 163.5 0.5 160.696 0.5 157.236V7.21191C0.5 3.7526 3.30436 0.948242 6.76367 0.948242Z"
      fill="white"
    />
    <path
      d="M6.76367 0.948242H179.236C182.696 0.948242 185.5 3.7526 185.5 7.21191V157.236C185.5 160.696 182.696 163.5 179.236 163.5H6.76367C3.30436 163.5 0.5 160.696 0.5 157.236V7.21191C0.5 3.7526 3.30436 0.948242 6.76367 0.948242Z"
      stroke="#E0E0E0"
    />
    <circle cx="28" cy="36.4482" r="12" fill="#D9D9D9" />
    <rect x="16" y="59.1943" width="140" height="10" rx="5" fill="#EDEDED" />
    <rect x="16" y="79.1943" width="56" height="10" rx="5" fill="#EDEDED" />
    <rect x="16" y="99.2139" width="154" height="6" rx="3" fill="#EDEDED" />
    <rect x="16" y="115.234" width="154" height="6" rx="3" fill="#EDEDED" />
    <rect x="16" y="131.254" width="111" height="6" rx="3" fill="#EDEDED" />
  </svg>
)

const calculateCreatorCampDayNumber = (todayDate: string): string => {
  const startDate = new Date('2025-06-16')
  const duration = new Date(todayDate).getTime() - startDate.getTime()
  const durationInDays = Math.floor(duration / (1000 * 60 * 60 * 24))
  return `${durationInDays + 1}`
}

export const DailyPromptBanner = ({
  template,
  todayDate,
}: {
  template: CreatorCampTemplate
  todayDate: string
}): JSX.Element => {
  const creatorCampInternalDateSelectorEnabled = useIsSplitEnabled(
    'creator_camp_internal_date_selector',
  )
  const organizationId = useOrganizationId()
  const isMobile = useMediaQuery('(width < 942px)')
  const trackCreatePostClick = (): void => {
    // <NewPostComposerTrigger> triggers opening the new post composer
    // so we just need to close the dialog and track the action here
    BufferTracker.templateUsed({
      cta: 'create-templates-dailyPrompt-createPostButton-1',
      organizationId: organizationId ?? '',
      product: 'publish',
      templateId: template.id,
      templateUse: 'createPost',
    })
  }

  return (
    <PromotionalBanner
      className={clsx(styles.dailyPromptBanner, isMobile && styles.responsive)}
    >
      <PromotionalBanner.Content className={styles.dailyPromptBannerContent}>
        <Flex align="center" gap="2xs">
          <Text size="md">
            Day {calculateCreatorCampDayNumber(todayDate)} • Creator Camp
          </Text>
          <ToggleTip
            content={
              <Text>
                Creator Camp is a 30-day challenge from June 16 to July 16
                designed to help you post consistently with daily templates,
                inspiration, and community support.{' '}
                <Link
                  href="https://buffer.com/resources/creator-camp-announcement/"
                  external
                >
                  Learn more
                </Link>
              </Text>
            }
          >
            <IconButton label="Templates" variant="tertiary" size="small">
              <InfoIcon color="subtle" />
            </IconButton>
          </ToggleTip>
        </Flex>
        <PromotionalBanner.Heading className={styles.dailyPromptHeading}>
          Today&apos;s Prompt ✨
          {creatorCampInternalDateSelectorEnabled && (
            <Badge variant="brand-subtle" size="medium">
              <Text weight="bold" size="sm">
                For Tami:
              </Text>{' '}
              Copy today&apos;s prompt link
              <IconButton
                label="Copy"
                variant="tertiary"
                size="small"
                onClick={(): Promise<void> =>
                  navigator.clipboard.writeText(
                    `https://publish.buffer.com/create/templates/template/${template.id}?cta=create-templates-dailyPrompt-sharedLink-1`,
                  )
                }
              >
                <CopyIcon color="brand" />
              </IconButton>
            </Badge>
          )}
        </PromotionalBanner.Heading>
        <PromotionalBanner.Description
          size="lg"
          className={styles.dailyPromptDescription}
        >
          {template.description}
        </PromotionalBanner.Description>
        <PromotionalBanner.Actions>
          <NewPostComposerTrigger
            prefillPostData={{ text: template.body }}
            cta="create-templates-dailyPrompt-createPostButton-1"
            templateId={template.id}
          >
            <Button size="large" onClick={trackCreatePostClick}>
              Create Post
            </Button>
          </NewPostComposerTrigger>
        </PromotionalBanner.Actions>
      </PromotionalBanner.Content>
      <Flex
        className={styles.templatePreviewSection}
        align="center"
        justify={isMobile ? 'center' : 'start'}
      >
        <Flex className={styles.bannerTemplateContainer}>
          <Flex className={styles.svgIllustrationContainer}>
            <CardIllustration />
          </Flex>
          <TemplateMiniCardLink
            templateId={template.id}
            className={styles.bannerTemplateButton}
            cta="create-templates-dailyPrompt-templatePreview-1"
          >
            <TemplateMiniCard
              template={template}
              className={styles.bannerTemplate}
            />
          </TemplateMiniCardLink>
        </Flex>
      </Flex>
    </PromotionalBanner>
  )
}
